import { Capacitor } from '@capacitor/core'
import { Purchases } from '@revenuecat/purchases-capacitor'

const PLATFORM_KEY_MAP = Object.freeze({
  ios: process.env.REACT_APP_REVENUECAT_IOS_API_KEY,
  android: process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY,
  web: process.env.REACT_APP_REVENUECAT_WEB_API_KEY,
  default: process.env.REACT_APP_REVENUECAT_API_KEY,
})

const MEMBERSHIP_PATTERNS = [
  { membership: 'ELITE', patterns: ['ELITE', 'ULTRA', 'VIP'] },
  { membership: 'PRO', patterns: ['PRO', 'PREMIUM', 'PLUS'] },
  { membership: 'FREE', patterns: ['FREE', 'BASIC', 'STARTER'] },
]

const MEMBERSHIP_STORAGE_KEYS = Object.freeze(['membership', 'membershipType'])
const MEMBERSHIP_EVENT = 'app:membership-updated'

let revenueCatConfigured = false
let configurePromise = null
let configuredUserId = null

const isBrowser = typeof window !== 'undefined'

const getPlatformKey = () => {
  const platform = Capacitor?.getPlatform?.() ?? 'web'
  const candidateOrder = [platform, 'default', 'web', 'ios', 'android']
  for (const key of candidateOrder) {
    const apiKey = PLATFORM_KEY_MAP[key]
    if (apiKey) {
      return { apiKey, platform }
    }
  }
  return { apiKey: '', platform }
}

const normalizeString = (value) => {
  if (!value && value !== 0) return ''
  return String(value).trim().toUpperCase()
}

const addCandidateValues = (target, values) => {
  if (!Array.isArray(values)) return
  values.forEach((value) => {
    const normalized = normalizeString(value)
    if (normalized) {
      target.add(normalized)
    }
  })
}

const dispatchMembershipEvent = () => {
  if (!isBrowser) return
  try {
    window.dispatchEvent(new Event(MEMBERSHIP_EVENT))
  } catch (error) {
    console.warn('Unable to dispatch membership update event', error)
  }
}

export const isRevenueCatAvailable = () => {
  if (!Purchases) {
    return false
  }
  const canConfigure = typeof Purchases.configure === 'function'
  const canFetchInfo = typeof Purchases.getCustomerInfo === 'function'
  return canConfigure && canFetchInfo
}

export const getRevenueCatUserId = () => {
  if (!isBrowser) {
    return undefined
  }
  const accountId = window.localStorage.getItem('account_ID')
  if (accountId) {
    return accountId
  }
  const mail = window.localStorage.getItem('mail')
  if (mail) {
    return mail
  }
  return undefined
}

export const ensureRevenueCatConfigured = async ({ appUserId } = {}) => {
  if (!isRevenueCatAvailable()) {
    return false
  }

  const targetUser = appUserId || getRevenueCatUserId()

  if (revenueCatConfigured) {
    if (targetUser && configuredUserId !== targetUser && typeof Purchases.logIn === 'function') {
      try {
        await Purchases.logIn(targetUser)
        configuredUserId = targetUser
      } catch (error) {
        console.warn('RevenueCat logIn failed', error)
      }
    }
    return true
  }

  if (configurePromise) {
    return configurePromise
  }

  configurePromise = (async () => {
    const { apiKey, platform } = getPlatformKey()
    if (!apiKey) {
      console.warn(`RevenueCat API key is missing for platform "${platform}"`)
      return false
    }

    try {
      await Purchases.configure({
        apiKey,
        appUserID: targetUser || undefined,
        shouldShowInAppMessagesAutomatically: false,
      })
      revenueCatConfigured = true
      configuredUserId = targetUser || null
      return true
    } catch (error) {
      console.error('RevenueCat configuration failed', error)
      return false
    }
  })().finally(() => {
    configurePromise = null
  })

  return configurePromise
}

export const getRevenueCatCustomerInfo = async () => {
  if (!isRevenueCatAvailable()) {
    return null
  }
  try {
    return await Purchases.getCustomerInfo()
  } catch (error) {
    console.error('Unable to fetch RevenueCat customer info', error)
    return null
  }
}

export const resolveMembershipFromCustomerInfo = (customerInfo) => {
  if (!customerInfo) {
    return ''
  }

  const candidates = new Set()
  const activeEntitlements = customerInfo?.entitlements?.active || {}

  Object.values(activeEntitlements).forEach((entitlement) => {
    if (!entitlement) return
    addCandidateValues(candidates, [
      entitlement.identifier,
      entitlement.productIdentifier,
      entitlement.offerIdentifier,
    ])
  })

  addCandidateValues(candidates, customerInfo?.activeSubscriptions)
  addCandidateValues(candidates, customerInfo?.allPurchasedProductIdentifiers)

  for (const candidate of candidates) {
    for (const { membership, patterns } of MEMBERSHIP_PATTERNS) {
      if (patterns.some((pattern) => candidate.includes(pattern))) {
        return membership
      }
    }
  }

  return ''
}

export const applyRevenueCatEntitlements = (customerInfo) => {
  if (!isBrowser) {
    return ''
  }

  const membership = resolveMembershipFromCustomerInfo(customerInfo)
  if (!membership) {
    return ''
  }

  let hasChanged = false
  MEMBERSHIP_STORAGE_KEYS.forEach((key) => {
    const currentValue = window.localStorage.getItem(key)
    if (currentValue !== membership) {
      window.localStorage.setItem(key, membership)
      hasChanged = true
    }
  })

  window.localStorage.setItem('membershipLastSyncedAt', new Date().toISOString())

  if (hasChanged) {
    dispatchMembershipEvent()
  }

  return membership
}

export const resetRevenueCatState = async () => {
  if (!isRevenueCatAvailable()) {
    return
  }
  try {
    if (typeof Purchases.logOut === 'function') {
      await Purchases.logOut()
    }
  } catch (error) {
    console.warn('RevenueCat logOut failed', error)
  } finally {
    revenueCatConfigured = false
    configurePromise = null
    configuredUserId = null
  }
}
